import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { UserSettingElementType } from "shared/Types/appTypes"
import { Logger } from "shared/Helpers/logging"
import { useFirebaseMessaging } from "shared/Modules/Cordova/hooks/useFirebase"
import { useEnvironment } from "shared/Modules/Environment/envHooks"
import { ErrorDisplay } from "shared/Modules/Error/errorTypes"
import { useToken } from "shared/Modules/Login/useToken"
import { useApiCall } from "shared/Modules/Query/useApiCall"
import { getUser } from "shared/Modules/User/userActions"
import { saveUserSetting } from "shared/Modules/User/userAPI"
import {
    ACCEPT_PUSH_FOR_NEWS_SETTING,
    ACCEPT_PUSH_FOR_TX_SETTING,
    ACCEPT_PUSH_NOTIFICATIONS_SETTING,
    selectPushNotificationSettings,
} from "shared/Modules/User/userSelectors"

export type PushNotificationTypes = Readonly<{
    forTx: boolean
    forNews: boolean
}>

// Defaults for a user who has not chosen yet: operational messages on, marketing must be opted into
export const DEFAULT_PUSH_NOTIFICATION_TYPES: PushNotificationTypes = { forTx: true, forNews: false }

type UsePushPermissionOptions = Readonly<{
    errorDisplay?: ErrorDisplay
}>

/**
 * Everything around the user's push notification consent: the saved settings, requesting the OS
 * permission and persisting the answer. Used by the onboarding step, the settings page and API prompts.
 *
 * After every save the user is reloaded so `useAppNotifications` sees the new consent immediately and
 * registers (or deletes) the FCM token right away.
 */
export function usePushPermission({ errorDisplay = ErrorDisplay.Snackbar }: UsePushPermissionOptions = {}) {
    const logger = new Logger("push-permission")

    const dispatch = useDispatch()
    const token = useToken()
    const { currentEnv } = useEnvironment()
    const firebase = useFirebaseMessaging()
    const settings = useSelector(selectPushNotificationSettings)

    const [requesting, setRequesting] = useState(false)
    // What the OS says right now (undefined until known / when the plugin is missing)
    const [osPermission, setOsPermission] = useState<boolean>()

    const { loading: saving, callForAction: callSaveSetting, handleCallError } = useApiCall(saveUserSetting, { errorDisplay })

    const saveSetting = useCallback((name: string, value: boolean) => {
        logger.info(`Saving user setting ${name}: ${value}`)
        return callSaveSetting({ name, value: String(value), elementType: UserSettingElementType.TOGGLE }, token, currentEnv)
    }, [callSaveSetting, token, currentEnv])

    const refreshOsPermission = useCallback(() => {
        if (!firebase) return
        firebase.hasPermission()
            .then((hasPermission) => setOsPermission(hasPermission))
            .catch((e) => logger.warn("Failed to read notification permission from OS", e))
    }, [firebase])

    // Read the OS state on mount and whenever the app comes back to the foreground (the user may have
    // changed it in the OS settings meanwhile)
    useEffect(() => {
        refreshOsPermission()
        document.addEventListener("resume", refreshOsPermission)
        return () => document.removeEventListener("resume", refreshOsPermission)
    }, [refreshOsPermission])

    // Keep the saved consent in line with what the OS says, so the token gets registered when the user
    // turns notifications on in the OS settings after declining once (and deleted when turned off).
    // A user who was never asked (accept undefined) is left alone so the onboarding step can still ask.
    useEffect(() => {
        if (osPermission === undefined || saving) return
        if (osPermission && settings.accept !== true) {
            logger.info("OS allows notifications but consent is not saved [saving consent]")
            saveSetting(ACCEPT_PUSH_NOTIFICATIONS_SETTING, true)
                .then(() => dispatch(getUser()))
                .catch((e) => handleCallError(e, "saving notification consent"))
        } else if (!osPermission && settings.accept === true) {
            logger.info("OS no longer allows notifications [withdrawing consent]")
            saveSetting(ACCEPT_PUSH_NOTIFICATIONS_SETTING, false)
                .then(() => dispatch(getUser()))
                .catch((e) => handleCallError(e, "saving notification consent"))
        }
    }, [osPermission, settings.accept])

    const saveType = useCallback((name: typeof ACCEPT_PUSH_FOR_TX_SETTING | typeof ACCEPT_PUSH_FOR_NEWS_SETTING, value: boolean) => {
        return saveSetting(name, value)
            .then(() => dispatch(getUser()))
            .catch((e) => handleCallError(e, "saving push notification type"))
    }, [saveSetting, dispatch, handleCallError])

    /**
     * Shows the OS permission dialog and persists the outcome as ACCEPT_PUSH_NOTIFICATIONS (plus the
     * chosen types when given). Resolves with whether permission was granted; resolves with undefined
     * when the permission cannot be requested (no Firebase plugin, e.g. in the browser).
     */
    const requestPermission = useCallback(async (types?: PushNotificationTypes): Promise<boolean | undefined> => {
        if (!firebase) {
            logger.warn("Firebase not available [cannot request notification permission]")
            return undefined
        }

        setRequesting(true)
        let granted = false
        try {
            await firebase.requestPermission()
            granted = true
            logger.info("Notification permission granted")
        } catch (e) {
            logger.info("Notification permission denied by user", e)
        }

        try {
            const saves = [saveSetting(ACCEPT_PUSH_NOTIFICATIONS_SETTING, granted)]
            if (types) {
                saves.push(saveSetting(ACCEPT_PUSH_FOR_TX_SETTING, types.forTx))
                saves.push(saveSetting(ACCEPT_PUSH_FOR_NEWS_SETTING, types.forNews))
            }
            await Promise.all(saves)
            // Reload user so consent (and thereby token registration) takes effect immediately
            dispatch(getUser())
        } catch (e) {
            handleCallError(e, "saving notification consent")
        } finally {
            // Only now, so the sync effect above sees consent and OS state change together
            setOsPermission(granted)
            setRequesting(false)
        }

        return granted
    }, [firebase, saveSetting, dispatch, handleCallError])

    return {
        settings,
        // Actual OS permission state; falls back to the saved consent when unknown
        osPermission: osPermission ?? settings.accept,
        refreshOsPermission,
        // Whether the OS permission can be requested at all (only inside the app with the Firebase plugin)
        canRequestPermission: Boolean(firebase),
        requesting,
        saving,
        requestPermission,
        saveType,
    }
}
